/**
 * The Subskrypcja tab's wiring: two reads, one gate, one projection.
 *
 * It decides nothing — `model/subscriptionCard` turns the entitlement and the seat numbers into
 * what the tab and the plate draw, and this file is the query layer under it.
 *
 * ── One gate over BOTH reads ────────────────────────────────────────────────────────────────
 * The plan comes from `GET /me/entitlements` and every seat number from `GET /customer/seat-usage`.
 * A card drawn from the first of the two to land is a partial answer wearing the shape of a
 * complete one — the seat bar would draw against a cap nobody had confirmed — so the skeleton is
 * gated on their union and the projection is only built when both are in.
 *
 * ── A web subscription waits for a THIRD read, and its failure is nobody's ──────────────────
 * A web trial (R19) changes what the renewal row says, so for a web subscription the card also
 * waits for `GET /me/subscription/web/trial` — inside the same gate, so the row is drawn once, in
 * its final words. It is asked only once the entitlement says `Web`, and it is handed to the gate
 * as a read that can never be an ERROR: R18 makes the plain renewal row the answer when the provider
 * does not answer, so a failed trial read falls back silently instead of turning the tab red.
 *
 * ── It refetches when the tab comes back into view ──────────────────────────────────────────
 * The one thing that changes this answer happens somewhere else entirely: the trainer opens the
 * App Store, cancels or upgrades, and comes back to this browser tab. That is the browser's own
 * focus event, and TanStack's focus manager already listens for it — `refetchOnWindowFocus` is left
 * on app-wide for exactly this. Nothing is subscribed here by hand.
 */

import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { seatUsage, seatUsageQueryOptions } from '~/entities/client';

import { TrainWithMeDataEnumsSubscriptionChannel as Channel } from '~/shared/api/generated/model';

import { entitlementsQueryOptions, webTrialQueryOptions } from '../api/accountQueries';
import { subscriptionCard, type SubscriptionCard } from '../model/subscriptionCard';
import { useSectionRead, type SectionState } from './useSectionRead';

export interface AccountSubscriptionApi {
  readonly state: SectionState;
  /** Whether the wait belongs on screen. The two tabs fork their bars on this, not on `state`. */
  readonly waiting: boolean;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly onRetry: () => void;
  /** Present exactly when `state` is `ready`. */
  readonly card: SubscriptionCard | null;
}

/**
 * @param active whether Subskrypcja is the tab on screen.
 */
export function useSubscriptionCard(active: boolean): AccountSubscriptionApi {
  const { t, i18n } = useTranslation();

  const [entitlements, seats] = useQueries({
    queries: [
      { ...entitlementsQueryOptions(), enabled: active },
      { ...seatUsageQueryOptions(), enabled: active },
    ],
  });

  // A DISABLED query is `isPending` for ever, so the gate is handed the enablement beside it.
  const asksTrial =
    active && entitlements.data?.isFree === false && entitlements.data.channel === Channel.Web;
  const trial = useQuery({ ...webTrialQueryOptions(), enabled: asksTrial });

  const projection =
    entitlements.data === undefined || seats.data === undefined ? null : seatUsage(seats.data);

  const section = useSectionRead({
    active,
    reads: [
      entitlements,
      seats,
      {
        isPending: asksTrial && trial.isPending,
        isError: false,
        // `refetch` ignores `enabled`, so a retry pressed on a store subscription would otherwise
        // ask the provider about a trial no store subscription can have.
        refetch: () => (asksTrial ? trial.refetch() : undefined),
      },
    ],
    incomplete: projection === null,
  });

  return {
    ...section,
    // `waiting` and not `state`: the card is what REPLACES the bars, and the gate holds them for
    // up to 300ms after the read answers. Handing the card over early puts the seat caption and the
    // legend on screen beside a skeleton figure they are supposed to arrive with.
    card:
      section.waiting || projection === null || entitlements.data === undefined
        ? null
        : subscriptionCard({
            entitlements: entitlements.data,
            seats: projection,
            trial: asksTrial ? trial.data : undefined,
            locale: i18n.language,
            t,
          }),
  };
}
